import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../App";
import PortalWorkspaceVerificationGate from "../components/clientPortal/PortalWorkspaceVerificationGate";
import PortalExistingAccountEmailVerificationBanner from "../components/clientPortal/PortalExistingAccountEmailVerificationBanner";
import { resolveClientPortalProfile } from "../utils/clientPortalProfileResolve";
import {
  shouldBlockPortalWorkspaceAccess,
  shouldDeferPortalFirestoreReads,
} from "../utils/portalWorkspaceAccessGate";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import {
  clearAdminReturnPath,
  consumeAdminReturnPath,
  consumePortalEmailLoginPending,
  peekPortalEmailLoginPending,
} from "../utils/adminReturnIntent";
import {
  buildPortalReturnPathFromLocation,
  consumePortalReturnPath,
  storePortalReturnPath,
} from "../utils/portalReturnIntent";
import { clearPortalAuthSessionState } from "../utils/portalAuthErrors";
import {
  readPendingInviteCodeFromLocationSearch,
  buildClientPortalNavigateTargetPreservingSearch,
} from "../utils/portalInviteUrlAccessGate";
import { portalShellSublabelFromShellContext, displayNameFromProfile } from "../clientPortal/displayUtils";
import { CLIENT_PORTAL_PROFILE_PATH } from "../clientPortal/clientPortalPrimaryNav";
import {
  buildStaffCompaniesDirectoryNavItem,
  resolveStaffTradeDirectoryAccess,
} from "../utils/staffTradeDirectoryAccessModel";
import { clearInvoiceWorkflowSession } from "../utils/invoiceWorkflowSessionModel";
import {
  CLIENT_PORTAL_LOGIN_PATH,
  runClientPortalSignOutSequence,
} from "../utils/clientPortalSignOutModel";

function safeStr(value) {
  return (value ?? "").toString().trim();
}

function normalizeEmail(value) {
  return safeStr(value).toLowerCase();
}

function activeProjectIdFromPath(pathname) {
  const match = safeStr(pathname).match(/^\/client-portal\/projects\/([^/]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

/** CL-374A: top header section links (My projects / Real estate). */
const headerSectionLinkClass = ({ isActive }) =>
  `shrink-0 rounded-md px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] no-underline transition-colors whitespace-nowrap ${
    isActive
      ? "bg-slate-900 text-white shadow-sm"
      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
  }`;

const editProfileLinkClass = ({ isActive }) =>
  `shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] no-underline transition-colors whitespace-nowrap ${
    isActive
      ? "text-sky-900 underline underline-offset-4"
      : "text-sky-700 hover:text-sky-900 hover:underline underline-offset-4"
  }`;

/**
 * Client portal shell: persistent primary navigation (industry-style app header) + main content.
 */
export default function ClientPortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authResolved, setAuthResolved] = useState(false);
  const [user, setUser] = useState(() => auth.currentUser);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState("");
  const [activeProject, setActiveProject] = useState(null);
  const [activeMatchedCollaborator, setActiveMatchedCollaborator] = useState(null);
  const showExtendedNav = !!(user && !user.isAnonymous);
  const activeProjectId = useMemo(
    () => activeProjectIdFromPath(location.pathname),
    [location.pathname]
  );
  const online = useOnlineStatus();
  const [verificationOverride, setVerificationOverride] = useState(false);

  const pendingInviteCode = useMemo(
    () => readPendingInviteCodeFromLocationSearch(location.search),
    [location.search]
  );

  const blockWorkspaceAccess = useMemo(() => {
    if (verificationOverride) return false;
    return shouldBlockPortalWorkspaceAccess(user, { pathname: location.pathname });
  }, [user, location.pathname, verificationOverride]);

  const handleWorkspaceVerified = useCallback(async () => {
    setVerificationOverride(true);
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.isAnonymous) return;
    if (readPendingInviteCodeFromLocationSearch(location.search)) {
      return;
    }
    setProfileLoading(true);
    try {
      const resolved = await resolveClientPortalProfile(db, currentUser);
      setProfile(resolved.profile);
    } catch (e) {
      console.error("ClientPortalLayout profile load after verification:", e);
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, [location.search]);

  const portalUserLabel = useMemo(
    () => displayNameFromProfile(profile, user),
    [profile, user]
  );

  const isAdminRole = useMemo(
    () => (profile?.role || "").toString().toLowerCase() === "admin",
    [profile]
  );

  const portalBrandSublabel = useMemo(
    () =>
      portalShellSublabelFromShellContext({
        profile,
        project: activeProject,
        matchedCollaborator: activeMatchedCollaborator,
        viewer: user,
        isAnonymous: !!user?.isAnonymous,
        pathname: location.pathname,
      }),
    [profile, activeProject, activeMatchedCollaborator, user, location.pathname]
  );

  const tradeDirectoryAccess = useMemo(
    () => resolveStaffTradeDirectoryAccess({ userProfile: profile, isAdmin: false }),
    [profile]
  );

  const companiesDirectoryNavItem = useMemo(
    () => buildStaffCompaniesDirectoryNavItem(tradeDirectoryAccess),
    [tradeDirectoryAccess]
  );

  const { projectsNavActive, realEstateNavActive, companiesDirectoryNavActive } = useMemo(() => {
    const path = location.pathname || "";
    let ws = "";
    try {
      ws = new URLSearchParams(location.search || "").get("workspace") || "";
    } catch {
      ws = "";
    }
    return {
      projectsNavActive:
        path.startsWith("/client-portal/projects") ||
        path.startsWith("/client-portal/markup") ||
        (path === "/client-portal" && ws === "construction"),
      realEstateNavActive: path.startsWith("/client-portal/real-estate"),
      companiesDirectoryNavActive: path.startsWith("/client-portal/companies-suppliers"),
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      setAuthResolved(true);
      setUser(u);
      if (!u || u.isAnonymous) {
        setProfile(null);
        setProfileLoading(false);
        setVerificationOverride(false);
        return;
      }
      setVerificationOverride(false);
      if (shouldDeferPortalFirestoreReads(u, { pendingInviteCode })) {
        setProfile(null);
        setProfileLoading(false);
        return;
      }
      setProfileLoading(true);
      try {
        const resolved = await resolveClientPortalProfile(db, u);
        setProfile(resolved.profile);
      } catch (e) {
        console.error("ClientPortalLayout profile load:", e);
        setProfile(null);
      } finally {
        setProfileLoading(false);
      }
    });
    return () => unsub();
  }, [location.pathname, pendingInviteCode]);

  useEffect(() => {
    if (blockWorkspaceAccess || !user?.uid || user.isAnonymous || !activeProjectId) {
      setActiveProject(null);
      setActiveMatchedCollaborator(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "pmProjects", activeProjectId));
        if (cancelled) return;
        if (!snap.exists()) {
          setActiveProject(null);
          setActiveMatchedCollaborator(null);
          return;
        }
        const project = { id: snap.id, ...snap.data() };
        const viewerUid = safeStr(user.uid);
        const viewerEmail = normalizeEmail(user.email);
        const rows = Array.isArray(project.collaborators) ? project.collaborators : [];
        const matched =
          rows.find((row) => {
            const rowUid = safeStr(row?.uid);
            const rowEmail = normalizeEmail(row?.email || row?.emailLower);
            return (viewerUid && rowUid && rowUid === viewerUid) || (viewerEmail && rowEmail && rowEmail === viewerEmail);
          }) || null;
        setActiveProject(project);
        setActiveMatchedCollaborator(matched);
      } catch (e) {
        console.error("ClientPortalLayout active project load:", e);
        if (!cancelled) {
          setActiveProject(null);
          setActiveMatchedCollaborator(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blockWorkspaceAccess, user?.uid, user?.email, user?.isAnonymous, activeProjectId]);

  /** Stale admin deep-link intent should not apply to non-admin sessions. */
  useEffect(() => {
    if (!user || user.isAnonymous || profileLoading) return;
    if (!isAdminRole) clearAdminReturnPath();
  }, [user, profileLoading, isAdminRole]);

  /**
   * Admins never stay on the client shell: dashboard, stored admin return path (deep link), or
   * post–email-login handoff (CL-309.2 / CL-309.3).
   */
  useLayoutEffect(() => {
    if (!user || user.isAnonymous || profileLoading) return;
    if (!isAdminRole) {
      if (peekPortalEmailLoginPending()) {
        consumePortalEmailLoginPending();
        const storedReturn = consumePortalReturnPath();
        if (storedReturn) {
          navigate(storedReturn, { replace: true });
          return;
        }
        const path = location.pathname || "";
        if (path === "/client-portal" || path === "/client-portal/") {
          navigate(buildClientPortalNavigateTargetPreservingSearch(path, location.search), { replace: true });
        }
      }
      return;
    }
    if (peekPortalEmailLoginPending()) {
      consumePortalEmailLoginPending();
      const next = consumeAdminReturnPath();
      navigate(next || "/admin/dashboard", { replace: true });
      return;
    }
    navigate("/admin/dashboard", { replace: true });
  }, [user, profileLoading, isAdminRole, navigate, location.pathname, location.search]);

  const onSignOut = async () => {
    if (signingOut) return;
    setSignOutError("");
    setSigningOut(true);
    const uid = safeStr(user?.uid);
    const projectIdForSession = activeProjectId;
    try {
      const result = await runClientPortalSignOutSequence({
        signOutFn: () => signOut(auth),
        clearInvoiceSession: () => {
          if (projectIdForSession && uid) {
            clearInvoiceWorkflowSession(projectIdForSession, uid);
          }
        },
        clearAuthSession: () => clearPortalAuthSessionState(),
        navigate,
        loginPath: CLIENT_PORTAL_LOGIN_PATH,
      });
      if (!result.ok) {
        setSignOutError(result.message || "Sign out failed. You are still signed in.");
      }
    } finally {
      // Layout stays mounted on /client-portal; never leave signingOut stuck true.
      setSigningOut(false);
    }
  };

  /** After auth clears (or Back to a protected URL), never leave nested project content mounted. */
  useLayoutEffect(() => {
    if (!authResolved || user || signingOut) return;
    const path = location.pathname || "";
    if (path.startsWith("/client-portal/projects/")) {
      navigate(CLIENT_PORTAL_LOGIN_PATH, { replace: true });
    }
  }, [authResolved, user, signingOut, location.pathname, navigate]);

  if (!authResolved) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-100 px-4 text-slate-600">
        <p className="text-sm">Loading…</p>
      </div>
    );
  }

  if (user && !user.isAnonymous && profileLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-slate-100 text-slate-900">
        <div className="mx-auto flex max-w-7xl flex-1 items-center justify-center px-4 py-16 md:px-6">
          <p className="text-sm text-slate-600">Loading account…</p>
        </div>
      </div>
    );
  }

  if (user && !user.isAnonymous && isAdminRole) {
    return (
      <div className="flex min-h-screen flex-col bg-slate-100 text-slate-900">
        <div className="mx-auto flex max-w-7xl flex-1 items-center justify-center px-4 py-16 md:px-6">
          <p className="text-sm text-slate-600">Opening admin…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex w-full min-w-0 flex-col overflow-x-clip bg-slate-100 text-slate-900">
      <header
        className="sticky top-0 z-[100] w-full min-w-0 border-b border-slate-200/90 bg-white/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-white/90"
        role="banner"
      >
        {/* CL-374A: brand + section CTAs (left) · profile + account (right) */}
        <div className="mx-auto flex w-full min-w-0 max-w-7xl flex-wrap items-center justify-between gap-x-3 gap-y-3 px-4 py-3 md:gap-x-4 md:px-6">
          <div className="flex min-w-0 max-w-full flex-[1_1_16rem] flex-wrap items-center gap-x-3 gap-y-2 sm:gap-x-4">
            <Link
              to="/client-portal"
              className="group flex min-w-0 flex-col gap-0.5 no-underline"
              title="Portal home"
            >
              <span className="truncate text-lg font-semibold tracking-tight text-slate-900 group-hover:text-slate-700">
                Mark Beattie
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                {portalBrandSublabel}
              </span>
            </Link>

            {user ? (
              <nav
                className="flex min-w-0 flex-wrap items-center gap-1.5 sm:gap-2"
                aria-label="Portal sections"
              >
                <p className="sr-only">Move between My projects and Real estate.</p>
                <NavLink
                  end
                  to="/client-portal?workspace=construction"
                  className={() => headerSectionLinkClass({ isActive: projectsNavActive })}
                >
                  My projects
                </NavLink>
                {showExtendedNav ? (
                  <NavLink
                    to="/client-portal/real-estate"
                    className={() => headerSectionLinkClass({ isActive: realEstateNavActive })}
                  >
                    Real estate
                  </NavLink>
                ) : null}
                {companiesDirectoryNavItem ? (
                  <NavLink
                    to={companiesDirectoryNavItem.to}
                    className={() => headerSectionLinkClass({ isActive: companiesDirectoryNavActive })}
                    data-testid="client-portal-companies-suppliers-nav"
                  >
                    {companiesDirectoryNavItem.label}
                  </NavLink>
                ) : null}
                <a
                  href="/"
                  className="shrink-0 rounded-md px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 no-underline transition-colors whitespace-nowrap hover:bg-slate-100 hover:text-slate-900"
                >
                  <span className="sm:hidden">Site</span>
                  <span className="hidden sm:inline">Main website</span>
                </a>
              </nav>
            ) : null}
          </div>

          <div className="flex min-w-0 max-w-full flex-[1_1_12rem] flex-wrap items-center justify-end gap-2 md:gap-3">
            {user ? (
              <>
                <NavLink to={CLIENT_PORTAL_PROFILE_PATH} className={editProfileLinkClass}>
                  <span className="sm:hidden">Profile</span>
                  <span className="hidden sm:inline">Edit profile</span>
                </NavLink>
                <span
                  className="max-w-[7.5rem] truncate text-[11px] font-medium text-slate-600 sm:hidden"
                  title={user.email || portalUserLabel}
                >
                  {portalUserLabel}
                </span>
                <span
                  className="hidden max-w-[140px] truncate text-xs text-slate-500 sm:inline md:max-w-[220px]"
                  title={user.email || ""}
                >
                  {user.email}
                </span>
                <button
                  type="button"
                  onClick={onSignOut}
                  disabled={signingOut}
                  aria-busy={signingOut}
                  data-testid="client-portal-sign-out"
                  className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 disabled:cursor-wait disabled:opacity-70"
                >
                  {signingOut ? "Signing out…" : "Sign out"}
                </button>
              </>
            ) : (
              <>
                <a
                  href="/"
                  className="shrink-0 text-sm text-slate-500 underline-offset-4 hover:text-slate-800 hover:underline"
                >
                  Main website
                </a>
                <Link
                  to="/client-portal"
                  className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 no-underline"
                  onClick={() => {
                    const returnPath = buildPortalReturnPathFromLocation(location);
                    if (returnPath && returnPath !== "/client-portal" && returnPath !== "/client-portal/") {
                      storePortalReturnPath(returnPath);
                    }
                  }}
                  data-testid="portal-header-sign-in"
                >
                  Sign in
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {!online ? (
        <div
          className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-950"
          role="status"
          aria-live="polite"
        >
          You appear to be offline. Actions may fail until your connection returns.
        </div>
      ) : null}

      {signOutError ? (
        <div
          className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-center text-sm text-rose-950"
          role="alert"
          data-testid="client-portal-sign-out-error"
        >
          {signOutError}
        </div>
      ) : null}

      <div className="flex w-full min-w-0 flex-1 flex-col">
        {signingOut && user ? (
          <div
            className="mx-auto flex max-w-7xl flex-1 items-center justify-center px-4 py-16 text-sm text-slate-600"
            data-testid="client-portal-signing-out"
          >
            Signing out…
          </div>
        ) : !user && activeProjectId ? (
          // Auth cleared while still on a project URL — do not mount project detail interstitial.
          <div className="mx-auto flex max-w-7xl flex-1 items-center justify-center px-4 py-16 text-sm text-slate-600">
            Opening sign in…
          </div>
        ) : !user ? (
          // Login route: always mount Outlet even if signingOut was briefly sticky.
          <Outlet />
        ) : blockWorkspaceAccess && !user.isAnonymous ? (
          <PortalWorkspaceVerificationGate user={user} onVerified={handleWorkspaceVerified} />
        ) : (
          <>
            {!user.isAnonymous ? <PortalExistingAccountEmailVerificationBanner user={user} /> : null}
            <Outlet />
          </>
        )}
      </div>

      <footer className="mt-auto border-t border-slate-200/90 bg-white/80 px-4 py-3 text-center text-xs text-slate-500 md:px-6">
        <p>
          Shared project files are for your review on this portal. Use <span className="font-medium">Sign out</span> when
          using a shared or public device. Questions about data handling? Contact your project team.
        </p>
      </footer>
    </div>
  );
}
